import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Brain, Calendar, TrendingUp, Sparkles, MessageSquare, Save, Edit3, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const MoodTracker = ({ user }) => {
  const [moodScore, setMoodScore] = useState(5);
  const [selectedTags, setSelectedTags] = useState([]);
  const [journalEntry, setJournalEntry] = useState('');
  const [recentEntries, setRecentEntries] = useState([]);
  const [moodInsights, setMoodInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editJournalText, setEditJournalText] = useState('');

  const moodEmojis = [
    { score: 1, emoji: '😢', label: 'Very Low', color: 'text-red-500' },
    { score: 2, emoji: '😞', label: 'Low', color: 'text-red-400' },
    { score: 3, emoji: '😐', label: 'Okay', color: 'text-yellow-500' },
    { score: 4, emoji: '🙂', label: 'Good', color: 'text-yellow-400' },
    { score: 5, emoji: '😊', label: 'Happy', color: 'text-green-400' },
    { score: 6, emoji: '😄', label: 'Great', color: 'text-green-500' },
    { score: 7, emoji: '😆', label: 'Excellent', color: 'text-green-600' },
    { score: 8, emoji: '🤗', label: 'Amazing', color: 'text-emerald-500' },
    { score: 9, emoji: '🥰', label: 'Fantastic', color: 'text-emerald-600' },
    { score: 10, emoji: '🌟', label: 'Incredible', color: 'text-emerald-700' }
  ];

  const moodTags = [
    'Anxious', 'Calm', 'Stressed', 'Relaxed', 'Happy', 'Sad', 
    'Energetic', 'Tired', 'Motivated', 'Frustrated', 'Peaceful', 'Overwhelmed',
    'Confident', 'Insecure', 'Grateful', 'Lonely', 'Connected', 'Hopeful'
  ];

  useEffect(() => {
    fetchMoodData();
  }, []);

  const fetchMoodData = async () => {
    setLoading(true);
    try {
      const [entriesRes, insightsRes] = await Promise.all([
        axios.get('/mood/entries'),
        axios.get('/mood/insights')
      ]);
      setRecentEntries(entriesRes.data);
      setMoodInsights(insightsRes.data);
    } catch (error) {
      console.error('Error fetching mood data:', error);
      toast.error('Failed to load mood data');
    } finally {
      setLoading(false);
    }
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post('/mood/entry', {
        mood_score: moodScore,
        mood_tags: selectedTags,
        journal_entry: journalEntry
      });
      
      toast.success('Mood entry saved successfully!');
      
      // Reset form
      setMoodScore(5);
      setSelectedTags([]);
      setJournalEntry('');
      
      // Refresh data
      fetchMoodData();
    } catch (error) {
      console.error('Error saving mood entry:', error);
      toast.error('Failed to save mood entry');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCurrentMood = () => {
    return moodEmojis.find(mood => mood.score === moodScore);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry.id);
    setEditJournalText(entry.journal_entry || '');
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setEditJournalText('');
  };

  const handleSaveEdit = async (entryId) => {
    if (!editJournalText.trim()) {
      toast.error('Journal entry cannot be empty');
      return;
    }

    try {
      // Find the original entry to preserve mood_score and mood_tags
      const originalEntry = recentEntries.find(entry => entry.id === entryId);
      
      await axios.put(`/mood/${entryId}`, {
        mood_score: originalEntry.mood_score,
        mood_tags: originalEntry.mood_tags,
        journal_entry: editJournalText.trim()
      });
      
      toast.success('Journal entry updated successfully!');
      
      // Reset edit state
      setEditingEntry(null);
      setEditJournalText('');
      
      // Refresh data
      fetchMoodData();
    } catch (error) {
      console.error('Error updating journal entry:', error);
      toast.error('Failed to update journal entry');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="mood-tracker-loading">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-emerald-700 font-medium">Loading mood tracker...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-6 pb-12" data-testid="mood-tracker">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-emerald-800 mb-4">Mood Tracker</h1>
          <p className="text-lg text-emerald-600 max-w-2xl mx-auto">
            Track your emotional journey and receive AI-powered insights to support your mental wellness.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Mood Entry Form */}
          <div className="lg:col-span-2">
            <div className="card animate-fade-in-up">
              <div className="flex items-center space-x-3 mb-6">
                <Brain className="w-6 h-6 text-emerald-600" />
                <h2 className="text-xl font-semibold text-emerald-800">How are you feeling today?</h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Mood Scale */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-sm font-medium text-emerald-700">Current Mood</label>
                    <div className="text-right">
                      <div className={`text-4xl ${getCurrentMood()?.color}`}>{getCurrentMood()?.emoji}</div>
                      <div className="text-sm font-medium text-emerald-800">{getCurrentMood()?.label}</div>
                    </div>
                  </div>
                  
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={moodScore}
                      onChange={(e) => setMoodScore(parseInt(e.target.value))}
                      className="w-full h-3 bg-emerald-200 rounded-lg appearance-none cursor-pointer mood-slider"
                      data-testid="mood-slider"
                    />
                    <div className="flex justify-between text-xs text-emerald-600 mt-2">
                      <span>1 - Very Low</span>
                      <span>5 - Neutral</span>
                      <span>10 - Incredible</span>
                    </div>
                  </div>
                </div>

                {/* Mood Tags */}
                <div>
                  <label className="block text-sm font-medium text-emerald-700 mb-3">
                    What emotions are you experiencing? (select all that apply)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {moodTags.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleTagToggle(tag)}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                          selectedTags.includes(tag)
                            ? 'bg-emerald-600 text-white'
                            : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                        }`}
                        data-testid={`mood-tag-${tag.toLowerCase()}`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Journal Entry */}
                <div>
                  <label className="block text-sm font-medium text-emerald-700 mb-2">
                    Journal Entry (optional)
                  </label>
                  <textarea
                    value={journalEntry}
                    onChange={(e) => setJournalEntry(e.target.value)}
                    className="form-input min-h-[120px] resize-none"
                    placeholder="Share your thoughts, experiences, or anything that's on your mind today..."
                    data-testid="journal-entry"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full py-3 flex items-center justify-center space-x-2 disabled:opacity-50"
                  data-testid="save-mood-btn"
                >
                  {submitting ? (
                    <div className="spinner border-white"></div>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Save Mood Entry</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Mood Insights */}
            {moodInsights && (
              <div className="card animate-fade-in-up stagger-2">
                <div className="flex items-center space-x-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-lg font-semibold text-emerald-800">Your Insights</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="text-center p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg">
                    <div className="text-2xl font-bold text-emerald-800">
                      {moodInsights.average_mood}
                    </div>
                    <div className="text-sm text-emerald-600">7-Day Average</div>
                  </div>
                  
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <Sparkles className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-800">AI Suggestion</span>
                    </div>
                    <p className="text-sm text-emerald-700">{moodInsights.suggestion}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Entries */}
            <div className="card animate-fade-in-up stagger-3">
              <div className="flex items-center space-x-2 mb-4">
                <Calendar className="w-5 h-5 text-emerald-600" />
                <h3 className="text-lg font-semibold text-emerald-800">Recent Entries</h3>
              </div>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {recentEntries.length > 0 ? (
                  recentEntries.slice(0, 5).map((entry) => {
                    const mood = moodEmojis.find(m => m.score === entry.mood_score);
                    return (
                      <div key={entry.id} className="p-3 bg-emerald-50 rounded-lg" data-testid={`mood-entry-${entry.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className={`text-lg ${mood?.color}`}>{mood?.emoji}</span>
                            <span className="font-medium text-emerald-800">{mood?.label}</span>
                          </div>
                          <span className="text-xs text-emerald-600">
                            {formatDate(entry.created_at)}
                          </span>
                        </div>
                        
                        {entry.mood_tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {entry.mood_tags.slice(0, 3).map(tag => (
                              <span key={tag} className="px-2 py-1 bg-emerald-200 text-emerald-700 text-xs rounded">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {entry.journal_entry && (
                          <div className="space-y-2">
                            {editingEntry === entry.id ? (
                              <div className="space-y-2">
                                <textarea
                                  value={editJournalText}
                                  onChange={(e) => setEditJournalText(e.target.value)}
                                  className="w-full p-2 text-xs border border-emerald-300 rounded resize-none"
                                  rows="3"
                                  placeholder="Edit your journal entry..."
                                />
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleSaveEdit(entry.id)}
                                    className="flex items-center space-x-1 px-2 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700"
                                  >
                                    <Check className="w-3 h-3" />
                                    <span>Save</span>
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="flex items-center space-x-1 px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                                  >
                                    <X className="w-3 h-3" />
                                    <span>Cancel</span>
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start justify-between">
                                <p className="text-xs text-emerald-600 flex-1">
                                  {entry.journal_entry.length > 100 
                                    ? `${entry.journal_entry.substring(0, 100)}...` 
                                    : entry.journal_entry
                                  }
                                </p>
                                <button
                                  onClick={() => handleEditEntry(entry)}
                                  className="ml-2 p-1 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100 rounded"
                                  title="Edit journal entry"
                                >
                                  <Edit3 className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {entry.ai_insights && (
                          <div className="mt-2 p-2 bg-white rounded border-l-2 border-emerald-300">
                            <div className="flex items-center space-x-1 mb-1">
                              <Brain className="w-3 h-3 text-emerald-600" />
                              <span className="text-xs font-medium text-emerald-700">AI Insight</span>
                            </div>
                            <p className="text-xs text-emerald-600">
                              {entry.ai_insights.substring(0, 150)}...
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <MessageSquare className="w-8 h-8 text-emerald-300 mx-auto mb-2" />
                    <p className="text-sm text-emerald-600">No entries yet. Start tracking your mood!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoodTracker;